import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Modal, Select, Button } from '../../../components';
import { domiciliaryScheduleService } from '../../../services';
import { localTime } from '../../../utils/datetime';
import { toast } from 'react-hot-toast';

// Payroll export modal (mirrors the duty roster): period + employee filters,
// summary stats, a preview of payroll-ready rows, and a CSV download. Only
// completed/approved actual hours are payroll-ready; the rest are "needs review".
const SummaryCard = ({ label, value, tone = 'default' }) => {
  const tones = { default: 'text-gray-800 dark:text-gray-200', good: 'text-emerald-600', warn: 'text-amber-600' };
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
};

const ExportPayrollModal = ({ open, date, carers = [], onClose }) => {
  const [period, setPeriod] = useState('day');
  const [employeeId, setEmployeeId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    domiciliaryScheduleService.getPayroll(date, period, employeeId === 'all' ? undefined : employeeId)
      .then((res) => { if (res.success) setData(res.data); })
      .catch(() => toast.error('Failed to load payroll data'))
      .finally(() => setLoading(false));
  }, [open, date, period, employeeId]);

  if (!open) return null;

  const rows = data?.rows || [];
  const s = data?.summary || {};
  const periodLabel = period === 'week' ? 'This week' : 'Selected day';
  const empLabel = employeeId === 'all' ? 'All employees' : (carers.find((c) => String(c.id) === String(employeeId))?.name || '');

  const download = () => {
    if (rows.length === 0) return;
    const head = ['Employee', 'Zone', 'Client / Package', 'Work date', 'Pay code', 'Clock in', 'Clock out', 'Unpaid break (min)', 'Payable hours', 'Status'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = rows.map((r) => [r.employee, r.zone, r.client_package, r.work_date, r.pay_code, localTime(r.clock_in) || '', localTime(r.clock_out) || '', r.unpaid_break, r.payable_hours, r.status].map(esc).join(','));
    const csv = [head.map(esc).join(','), ...body].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url; link.download = `payroll-${period}-${date}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length > 1 ? 's' : ''}`);
  };

  return (
    <Modal isOpen onClose={onClose} hideHeader bodyClassName="flex flex-col flex-1 min-h-0" maxWidth="max-w-4xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 px-6 py-4">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-800 dark:text-gray-200">Export payroll hours</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">Payroll-ready timesheet data · {periodLabel} · {date} · {empLabel}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-md bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-600">CSV · UK 24-hour time</span>
            <button onClick={onClose} className="grid h-6 w-6 place-items-center rounded-full bg-blue-600 text-white hover:bg-blue-700"><X className="h-3.5 w-3.5" strokeWidth={3} /></button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Exports matched or manager-approved actual hours. Unpaid breaks are deducted automatically. Live shifts, future shifts and records needing review are not included in payroll.
          </p>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-6 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Export period</label>
              <Select
                className="w-[160px]"
                value={period}
                onChange={setPeriod}
                options={[{ value: 'day', label: 'Selected day' }, { value: 'week', label: 'This week' }]}
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Employee</label>
              <Select
                className="w-[220px]"
                value={employeeId}
                onChange={setEmployeeId}
                options={[{ value: 'all', label: 'All employees' }, ...carers.map((c) => ({ value: String(c.id), label: c.name }))]}
              />
            </div>
            <p className="max-w-xs text-[11px] text-gray-400 dark:text-gray-500">Exports payroll-ready actual hours for the {periodLabel.toLowerCase()}. Includes every employee with records in this period.</p>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryCard label="Records checked" value={s.records_checked || 0} />
            <SummaryCard label="Payroll-ready rows" value={s.payroll_ready || 0} tone="good" />
            <SummaryCard label="Total payable hours" value={(s.total_payable_hours || 0).toFixed(2)} tone="good" />
            <SummaryCard label={`Not included · ${s.not_included || 0} review`} value={s.not_included || 0} tone="warn" />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
            <table className="w-full min-w-[760px] text-left text-[11px]">
              <thead className="bg-gray-50 dark:bg-[#20242c] text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                <tr>
                  {['Employee', 'Zone', 'Client / Package', 'Work date', 'Pay code', 'Clock in', 'Clock out', 'Unpaid break', 'Payable hours', 'Status'].map((h) => (
                    <th key={h} className="px-3 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400 dark:text-gray-500">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400 dark:text-gray-500">No approved actual hours are available for this period and employee filter.</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-400">
                    <td className="px-3 py-2 font-semibold text-gray-800 dark:text-gray-200">{r.employee}</td>
                    <td className="px-3 py-2">{r.zone}</td>
                    <td className="px-3 py-2">{r.client_package}</td>
                    <td className="px-3 py-2">{r.work_date}</td>
                    <td className="px-3 py-2">{r.pay_code}</td>
                    <td className="px-3 py-2">{localTime(r.clock_in)}</td>
                    <td className="px-3 py-2">{localTime(r.clock_out)}</td>
                    <td className="px-3 py-2">{r.unpaid_break}m</td>
                    <td className="px-3 py-2 font-semibold text-gray-800 dark:text-gray-200">{r.payable_hours}</td>
                    <td className="px-3 py-2"><span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-600">{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="rounded-lg bg-gray-50 dark:bg-[#20242c] px-3 py-2 text-[11px] text-gray-400 dark:text-gray-500">
            <b>Worked-hours CSV:</b> employee, zone, client and care package, work date, actual clocking, unpaid break and payable hours. Package differences remain excluded until reviewed and approved.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={download} disabled={rows.length === 0}>
            {rows.length === 0 ? 'No payroll rows to export' : `Export ${rows.length} row${rows.length > 1 ? 's' : ''}`}
          </Button>
        </div>
    </Modal>
  );
};

export default ExportPayrollModal;
